import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

export interface StatusTabOption {
  value: string;
  label: string;
  color?: string;
}

/** "All" + one pill per status, with an optional count. Selection state is owned by the page. */
@Component({
  selector: 'app-status-tabs',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="pp-status-tabs" role="group" [attr.aria-label]="ariaLabel">
      <button
        *ngIf="showAll"
        type="button"
        class="pp-status-tab"
        [class.is-active]="!selectedList.length"
        [attr.aria-pressed]="!selectedList.length"
        (click)="reset.emit()">
        {{ allLabel }}
        <span *ngIf="counts" class="pp-status-tab__count">{{ countsPending ? '–' : (allCount ?? totalCount) }}</span>
      </button>
      <button
        *ngFor="let s of options"
        type="button"
        class="pp-status-tab"
        [class.is-active]="selectedList.includes(s.value)"
        [attr.aria-pressed]="selectedList.includes(s.value)"
        [style.--pp-tab-color]="s.color || null"
        (click)="toggle.emit(s.value)">
        <span class="pp-status-tab__dot"></span>
        {{ s.label }}
        <span *ngIf="counts" class="pp-status-tab__count">{{ countsPending ? '–' : (counts[s.value] || 0) }}</span>
      </button>
    </div>
  `,
  styles: [':host { display: block; min-width: 0; }'],
})
export class StatusTabsComponent {
  @Input() options: StatusTabOption[] = [];
  @Input() selected: string[] | string | null | undefined = null;
  @Input() counts: Record<string, number> | null = null;
  @Input() allCount: number | null = null;
  /** Show a dash in each count badge while counts are loading. */
  @Input() countsPending = false;
  @Input() showAll = true;
  @Input() allLabel = 'All';
  @Input() ariaLabel = 'Filter by status';

  @Output() toggle = new EventEmitter<string>();
  @Output() reset = new EventEmitter<void>();

  get selectedList(): string[] {
    if (Array.isArray(this.selected)) return this.selected;
    return this.selected ? [this.selected] : [];
  }

  get totalCount(): number {
    return Object.values(this.counts || {}).reduce((sum, n) => sum + (Number(n) || 0), 0);
  }
}
